import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Legend,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

/**
 * PieChart component for visualizing invoice status data
 * @param {Object} props
 * @param {Array} props.data - Array of invoice status data objects
 * @param {string} props.title - Chart title
 * @returns {React.Component} Rendered PieChart
 */
/**
 * Build the pie chart data model from the invoice report response.
 * Exported for unit tests (keeps tooltip math stable).
 *
 * @param {Array} data
 * @returns {Array<{name: string, value: number, amount: number}>}
 */
export function buildInvoiceStatusPieData(data = []) {
  if (!Array.isArray(data)) return [];

  return (
    data
      .map((item) => ({
        name: item.Name,
        // IMPORTANT: `Total` is already the aggregated count for the status.
        // Do not use per-status fields (ex: item.Paid) here because those can be
        // overwritten in the aggregation pipeline when multiple date buckets exist.
        value: Number(item.Total || 0),
        amount: Number(item.TotalAmount || 0),
      }))
      .filter((item) => item.value > 0) || []
  );
}

/**
 * Build tooltip display values from a recharts payload entry.
 * @param {Object} payloadEntry
 * @returns {{ name: string, count: number, amount: number } | null}
 */
export function getInvoiceStatusTooltipModel(payloadEntry) {
  const p = payloadEntry?.payload;
  if (!p) return null;
  return {
    name: p.name,
    count: Number(p.value || 0),
    amount: Number(p.amount || 0),
  };
}

export const PieChartComponent = ({ data = [], title }) => {
  // Custom colors for different statuses
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

  // Transform and filter data for the pie chart
  const chartData = useMemo(() => {
    return buildInvoiceStatusPieData(data);
  }, [data]);

  // Custom tooltip formatter - simplified to show only relevant status
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const tooltip = getInvoiceStatusTooltipModel(payload[0]);
      if (!tooltip) return null;
      return (
        <div className="custom-tooltip bg-white p-2 border border-gray-200 rounded shadow">
          <p className="font-bold">{`${tooltip.name}`}</p>
          <p>{`Count: ${tooltip.count}`}</p>
          <p>{`Amount: $${tooltip.amount.toFixed(2)}`}</p>
        </div>
      );
    }
    return null;
  };

  // Calculate total, paid, and unpaid amounts
  const { totalAmount, paidAmount, unpaidAmount } = useMemo(() => {
    // Ensure data is an array
    if (!Array.isArray(data)) {
      return { totalAmount: 0, paidAmount: 0, unpaidAmount: 0 };
    }

    return data.reduce(
      (acc, item) => ({
        totalAmount: acc.totalAmount + (item.TotalAmount || 0),
        paidAmount:
          acc.paidAmount + (item.Name === "Paid" ? item.TotalAmount || 0 : 0),
        unpaidAmount:
          acc.unpaidAmount + (item.Name !== "Paid" ? item.TotalAmount || 0 : 0),
      }),
      { totalAmount: 0, paidAmount: 0, unpaidAmount: 0 }
    );
  }, [data]);

  return !Array.isArray(data) || !data.length ? (
    <div className="text-center text-gray-500">
      No Data To Display For this Time Range
    </div>
  ) : (
    <div className="flex flex-col items-center">
      <div className="flex flex-col items-center gap-1 mb-2">
        <div className="text-lg font-semibold">
          Total Amount: ${totalAmount.toFixed(2)}
        </div>
        <div className="text-sm text-green-600">
          Paid Amount: ${paidAmount.toFixed(2)}
        </div>
        <div className="text-sm text-orange-600">
          Unpaid Amount: ${unpaidAmount.toFixed(2)}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            fill="#8884d8"
            label
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChartComponent;
